/**
 * Auth context - provides current user and auth state
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { UserRead } from '@/api/types'
import { STORAGE_KEYS, getItemFrom, setItemIn, clearAuthFrom } from '@/lib/storage'
import { userApi } from '@/api/endpoints/user'

/**
 * Records user activity for streak tracking.
 * Only pings the backend once per calendar day to avoid spamming.
 */
function recordDailyActivity(): void {
  const today = new Date().toISOString().split('T')[0] // "YYYY-MM-DD"
  const lastActivityDate =
    getItemFrom<string>(sessionStorage, STORAGE_KEYS.LAST_ACTIVITY_DATE) ??
    getItemFrom<string>(localStorage, STORAGE_KEYS.LAST_ACTIVITY_DATE)

  if (lastActivityDate === today) {
    // Already pinged today, skip
    return
  }

  // Ping the backend and update local storage
  userApi.recordActivity()
    .then(() => {
      // Track per-session for admins (sessionStorage) and per-browser for others (localStorage).
      // We don't know which store is "active" here, so write to both.
      setItemIn(sessionStorage, STORAGE_KEYS.LAST_ACTIVITY_DATE, today)
      setItemIn(localStorage, STORAGE_KEYS.LAST_ACTIVITY_DATE, today)
    })
    .catch((err) => {
      console.warn('Failed to record activity:', err)
    })
}

interface AuthContextValue {
  user: UserRead | null
  isAuthenticated: boolean
  isLoading: boolean
  setUser: (user: UserRead | null) => void
  login: (user: UserRead, accessToken: string, refreshToken: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRead | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    // Prefer sessionStorage (admin sessions are per-tab), fall back to localStorage.
    const token =
      getItemFrom<string>(sessionStorage, STORAGE_KEYS.ACCESS_TOKEN) ??
      getItemFrom<string>(localStorage, STORAGE_KEYS.ACCESS_TOKEN)
    const storedUser =
      getItemFrom<UserRead>(sessionStorage, STORAGE_KEYS.USER) ??
      getItemFrom<UserRead>(localStorage, STORAGE_KEYS.USER)
    if (token && storedUser) {
      setUser(storedUser)
      // Record activity for streak tracking (once per day)
      recordDailyActivity()
    }
    setIsLoading(false)
  }, [])

  // Clear user when the axios interceptor signals an unrecoverable 401.
  useEffect(() => {
    const handler = () => {
      setUser(null)
      queryClient.clear()
      // Show session expired toast
      const toast = document.createElement('div')
      toast.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl bg-destructive/90 text-white text-sm font-medium shadow-lg backdrop-blur-sm flex items-center gap-2'
      toast.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>Session expired. Please log in again.'
      document.body.appendChild(toast)
      setTimeout(() => toast.remove(), 5000)
      // Redirect to login
      setTimeout(() => {
        window.location.href = '/login'
      }, 1500)
    }
    window.addEventListener('auth:session-expired', handler)
    return () => window.removeEventListener('auth:session-expired', handler)
  }, [queryClient])

  const login = (user: UserRead, accessToken: string, refreshToken: string) => {
    // Drop any cached data from a prior session before populating the new one.
    queryClient.clear()

    const store = user.role === 'admin' ? sessionStorage : localStorage
    clearAuthFrom(store)
    setItemIn(store, STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    setItemIn(store, STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
    setItemIn(store, STORAGE_KEYS.USER, user)
    setUser(user)
    // Record activity for streak tracking on login
    recordDailyActivity()
  }

  const logout = () => {
    const store = user?.role === 'admin' ? sessionStorage : localStorage
    clearAuthFrom(store)
    setUser(null)
    queryClient.clear()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        setUser,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
